// Messages Service — Kişisel/Mağaza switcher modeli.
// Taraf: { type:'user'|'store', id }. sideKey = `${type}:${id}`.
// conversation doc id = pairKey (sıralı sideKey'ler) → dedup + yarış çözümü.
// Erişim: userUids (kişisel) + storeIds (mağaza, kuralda canlı sellerProfiles.memberUids).
import {
  collection, query, where, getDocs, addDoc, updateDoc, doc, orderBy,
  onSnapshot, getDoc, setDoc, serverTimestamp, increment, arrayUnion, arrayRemove,
} from 'firebase/firestore';
import { db } from '../firebase';
import { getMaintenanceSettings } from './admin';
import logger from '../utils/logger';

const MESSAGE_COOLDOWN = 1000;
const MAX_MESSAGE_LENGTH = 1000;
const lastMessageTimes = new Map();
const partyCache = new Map();
const PARTY_CACHE_TTL = 5 * 60 * 1000;

export const sideKey = (side) => `${side.type}:${side.id}`;
// D4: pairKey URL-güvenli — doc id'de ':' yok, '__' ayraçlı, ':' → '-'
const makePairKey = (a, b) => [sideKey(a), sideKey(b)].sort().join('__').split(':').join('-');

const sanitize = (t) => (!t || typeof t !== 'string') ? '' : t.trim().slice(0, MAX_MESSAGE_LENGTH);
const checkRate = (uid) => {
  const now = Date.now(); const last = lastMessageTimes.get(uid) || 0;
  if (now - last < MESSAGE_COOLDOWN) return false;
  lastMessageTimes.set(uid, now); return true;
};
const tsMillis = (t) => t?.toMillis?.() || (t?.seconds ? t.seconds * 1000 : 0);

/** Taraf id'sini görüntüye çevirir: önce sellerProfiles (mağaza), sonra users (kişi). */
const getCachedParty = async (side) => {
  const key = sideKey(side);
  const cached = partyCache.get(key);
  if (cached && Date.now() - cached.timestamp < PARTY_CACHE_TTL) return cached.data;
  let result = { name: 'Bilinmeyen', photoURL: '', type: side.type };
  try {
    if (side.type === 'store') {
      const s = await getDoc(doc(db, 'sellerProfiles', side.id));
      if (s.exists()) result = { name: s.data().publicName || 'Mağaza', photoURL: s.data().profileImageUrl || '', type: 'store' };
    } else {
      const u = await getDoc(doc(db, 'users', side.id));
      if (u.exists()) { const d = u.data(); result = { name: `${d.firstName || ''} ${d.lastName || ''}`.trim() || 'Bilinmeyen', photoURL: d.photoURL || '', type: 'user' }; }
    }
  } catch (e) { logger.error('getCachedParty error:', e); }
  partyCache.set(key, { data: result, timestamp: Date.now() });
  return result;
};

const partiesFields = (parties) => ({
  parties,
  userUids: parties.filter((p) => p.type === 'user').map((p) => p.id),
  storeIds: parties.filter((p) => p.type === 'store').map((p) => p.id),
});

/**
 * Konuşma başlat/getir. myUid = başlatan kişi (her zaman 'user' tarafı), target = {type,id}.
 * Self-DM (kullanıcı kendi mağazasına) engellenir.
 */
export const startConversation = async (myUid, target) => {
  try {
    if (!myUid || !target?.id) return { success: false, error: 'Geçersiz taraflar.' };
    const me = { type: 'user', id: myUid };
    if (sideKey(me) === sideKey(target)) return { success: false, error: 'Kendinize mesaj gönderemezsiniz.' };
    // Self-DM: kullanıcı kendi mağazasına (user:uid ↔ store:uid)
    if (target.type === 'store' && target.id === myUid) return { success: false, error: 'Kendi mağazanıza mesaj gönderemezsiniz.' };
    const maintenance = await getMaintenanceSettings();
    if (maintenance.success && maintenance.data?.enabled) return { success: false, error: 'Site bakımda. Mesajlaşma kapalı.' };

    const parties = [me, target];
    const pairKey = makePairKey(me, target);
    const ref = doc(db, 'conversations', pairKey);
    const snap = await getDoc(ref);
    if (snap.exists()) return { success: true, conversationId: pairKey, data: snap.data() };

    const f = partiesFields(parties);
    const newConv = {
      ...f, pairKey,
      lastMessage: '', lastMessageTime: serverTimestamp(), createdAt: serverTimestamp(),
      unreadCount: { [sideKey(me)]: 0, [sideKey(target)]: 0 },
      hiddenBy: [],
    };
    await setDoc(ref, newConv);
    return { success: true, conversationId: pairKey, data: newConv };
  } catch (e) { logger.error('startConversation error:', e); return { success: false, error: 'Konuşma başlatılamadı.' }; }
};

/**
 * Mesaj gönder. mySide/otherSide = {type,id}; senderUserId = fiilen yazan kişi uid.
 */
export const sendMessage = async (conversationId, mySide, otherSide, text, senderUserId) => {
  try {
    const maintenance = await getMaintenanceSettings();
    if (maintenance.success && maintenance.data?.enabled) return { success: false, error: 'Site bakımda.' };
    if (!checkRate(senderUserId || mySide.id)) return { success: false, error: 'Çok hızlı mesaj gönderiyorsunuz.' };
    const body = sanitize(text);
    if (!body) return { success: false, error: 'Mesaj boş olamaz.' };

    const convRef = doc(db, 'conversations', conversationId);
    const convSnap = await getDoc(convRef);
    if (!convSnap.exists()) return { success: false, error: 'Konuşma bulunamadı.' };
    const conv = convSnap.data();
    const storeId = (conv.storeIds && conv.storeIds[0]) || null;

    await addDoc(collection(db, 'messages'), {
      conversationId,
      text: body, createdAt: serverTimestamp(), read: false,
      senderSideKey: sideKey(mySide), receiverSideKey: sideKey(otherSide),
      senderUserId: senderUserId || mySide.id,
      userUids: conv.userUids || [], storeId,
    });

    await updateDoc(convRef, {
      lastMessage: body, lastMessageTime: serverTimestamp(),
      [`unreadCount.${sideKey(otherSide)}`]: increment(1),
      hiddenBy: arrayRemove(sideKey(mySide), sideKey(otherSide)), // yeni mesaj → iki taraf için tekrar görünür
    });
    return { success: true };
  } catch (e) { logger.error('sendMessage error:', e); return { success: false, error: 'Mesaj gönderilemedi.' }; }
};

/** Bir kutudaki konuşmaları (real-time) getir. inbox: 'user' → Ben (myUid); 'store' → Mağaza (myStoreId). */
export const getInboxConversations = (inbox, { myUid, myStoreId }, callback) => {
  const ref = collection(db, 'conversations');
  let q;
  if (inbox === 'store') {
    if (!myStoreId) { callback([]); return () => {}; }
    q = query(ref, where('storeIds', 'array-contains', myStoreId));
  } else {
    q = query(ref, where('userUids', 'array-contains', myUid));
  }
  const mySide = inbox === 'store' ? { type: 'store', id: myStoreId } : { type: 'user', id: myUid };
  const myKey = sideKey(mySide);
  const unsub = onSnapshot(q, async (snap) => {
    try {
      const visible = snap.docs.filter((d) => !(Array.isArray(d.data().hiddenBy) && d.data().hiddenBy.includes(myKey)));
      const convos = await Promise.all(visible.map(async (d) => {
        const data = d.data();
        const otherParty = (data.parties || []).find((p) => sideKey(p) !== myKey) || (data.parties || [])[0];
        const other = otherParty ? await getCachedParty(otherParty) : { name: 'Bilinmeyen', photoURL: '', type: 'user' };
        return {
          id: d.id, ...data, mySideKey: myKey, myInbox: inbox,
          otherPartyType: other.type, otherUserName: other.name, otherUserPhotoURL: other.photoURL,
          unreadCount: data.unreadCount?.[myKey] || 0,
        };
      }));
      convos.sort((a, b) => tsMillis(b.lastMessageTime) - tsMillis(a.lastMessageTime));
      callback(convos);
    } catch (e) { logger.error('getInboxConversations error:', e); callback([]); }
  }, (e) => { logger.error('inbox listener error:', e); callback([]); });
  return unsub;
};

/**
 * Bir konuşmanın mesajları (real-time).
 * KURAL HİZALAMASI: Firestore liste-sorgusunda kural `resource.data` alanına bakıyorsa sorgu o alanı
 * KISITLAMALI. messages.read kuralı storeId/userUids'e bakar → sorguya yan-bazlı ek filtre koyarız:
 * mağaza tarafı `storeId==mySide.id`, müşteri tarafı `userUids array-contains mySide.id`.
 */
export const getConversationMessages = (conversationId, callback, currentUserId, mySide) => {
  try {
    if (!mySide?.type || !mySide?.id) { callback([]); return () => {}; }
    const base = collection(db, 'messages');
    const q = mySide.type === 'store'
      ? query(base, where('conversationId', '==', conversationId), where('storeId', '==', mySide.id), orderBy('createdAt', 'asc'))
      : query(base, where('conversationId', '==', conversationId), where('userUids', 'array-contains', mySide.id), orderBy('createdAt', 'asc'));
    return onSnapshot(q, (snap) => {
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(all.filter((m) => !(Array.isArray(m.deletedBy) && m.deletedBy.includes(currentUserId))));
    }, (e) => { logger.error('messages listener error:', e); callback([]); });
  } catch (e) { logger.error('getConversationMessages error:', e); return () => {}; }
};

/**
 * Okundu işaretle (paylaşımlı): mySideKey'e gelen okunmamışları okundu yap + sayaç sıfırla.
 * Sorgu yan-bazlı hizalanır (kural hizalaması — bkz. getConversationMessages); read/receiver filtresi JS'te.
 */
export const markMessagesAsRead = async (conversationId, mySideKey, mySide) => {
  try {
    if (!mySide?.type || !mySide?.id) return { success: false };
    const base = collection(db, 'messages');
    const q = mySide.type === 'store'
      ? query(base, where('conversationId', '==', conversationId), where('storeId', '==', mySide.id))
      : query(base, where('conversationId', '==', conversationId), where('userUids', 'array-contains', mySide.id));
    const snap = await getDocs(q);
    const unread = snap.docs.filter((m) => { const d = m.data(); return d.receiverSideKey === mySideKey && d.read === false; });
    await Promise.all(unread.map((m) => updateDoc(doc(db, 'messages', m.id), { read: true })));
    await updateDoc(doc(db, 'conversations', conversationId), { [`unreadCount.${mySideKey}`]: 0 });
    return { success: true };
  } catch (e) { logger.error('markMessagesAsRead error:', e); return { success: false }; }
};

/** İki kutunun toplam okunmamışı (navbar/bottom bar zili). */
export const getTotalUnreadCount = (myUid, myStoreId, callback) => {
  const ref = collection(db, 'conversations');
  const queries = [query(ref, where('userUids', 'array-contains', myUid))];
  if (myStoreId) queries.push(query(ref, where('storeIds', 'array-contains', myStoreId)));
  const results = queries.map(() => []); const ready = queries.map(() => false);
  const merge = () => {
    if (!ready.every(Boolean)) return;
    const seen = new Set(); let total = 0;
    results.forEach((arr, qi) => {
      const myKey = qi === 1 ? `store:${myStoreId}` : `user:${myUid}`;
      arr.forEach((d) => { if (seen.has(d.id + myKey)) return; seen.add(d.id + myKey); total += d.data().unreadCount?.[myKey] || 0; });
    });
    callback(total);
  };
  const unsubs = queries.map((q, i) => onSnapshot(q, (snap) => { results[i] = snap.docs; ready[i] = true; merge(); }, () => { ready[i] = true; merge(); }));
  return () => unsubs.forEach((u) => u && u());
};

/** Konuşmayı bu taraf (sideKey) için gizle. */
export const hideConversation = async (conversationId, mySideKey) => {
  try { await updateDoc(doc(db, 'conversations', conversationId), { hiddenBy: arrayUnion(mySideKey) }); return { success: true }; }
  catch (e) { logger.error('hideConversation error:', e); return { success: false }; }
};

/** Mesajı kullanıcı için sil (deletedBy). */
export const deleteMessageForUser = async (messageId, userId) => {
  try { await updateDoc(doc(db, 'messages', messageId), { deletedBy: arrayUnion(userId) }); return { success: true }; }
  catch (e) { logger.error('deleteMessageForUser error:', e); return { success: false }; }
};
